import React, { useState, useEffect } from "react";
import { Y, T, CREDIT } from "../theme";
import { useTheme } from "../useTheme";
import { api, auth, signOutUser, linkDiscordToCurrentUser, unlinkDiscordFromCurrentUser, linkGoogleToCurrentUser, unlinkGoogleFromCurrentUser } from "../firebase";
import { useNotify, useHeaderRight, useRefreshProfile } from "../App";
import PageLoader from "./PageLoader";

const PlaceholderStyle = () => <style>{`
input::placeholder { color: ${T.text3} !important; opacity: 0.6; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.s-edit { transition: color 0.15s !important; }
.s-edit:hover { color: ${T.title} !important; }
.s-del { transition: opacity 0.15s !important; }
.s-del:hover { opacity: 0.7 !important; }
.s-cancel { transition: color 0.15s !important; }
.s-cancel:hover { color: ${T.text2} !important; }
.s-tab { transition: background 0.15s, color 0.15s, border-color 0.15s !important; }
.s-tab:hover { background: ${T.surface2} !important; }
.s-toggle { transition: background 0.2s, border-color 0.2s !important; }
`}</style>;

function GoogleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style={{color:T.text2}}>
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
}

function DiscordIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="#5865F2">
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.07.07 0 0 0-.074.035c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.074-.035 19.74 19.74 0 0 0-4.885 1.515.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.331c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}

function ProviderRow({ icon, label, detail, actionLabel, onAction, actionDanger, busy }) {
  return (
    <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:"transparent",border:`1px solid ${T.border}`,borderRadius:10}}>
      <div style={{flexShrink:0,display:"flex"}}>{icon}</div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{color:T.text2,fontSize:13,fontWeight:600}}>{label}</div>
        <div style={{color:T.text3,fontSize:11,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
          {detail || (actionLabel ? "Not connected" : "Connected")}
        </div>
      </div>
      {actionLabel && (
        <button
          onClick={onAction}
          disabled={busy}
          style={{
            background:"transparent",
            border:`1px solid ${actionDanger ? T.danger : T.border}`,
            color: actionDanger ? T.danger : T.text2,
            fontSize:12, fontWeight:600,
            padding:"6px 14px", borderRadius:8,
            cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.6 : 1,
            fontFamily:"Mulish,sans-serif",
            whiteSpace:"nowrap",
          }}>
          {busy ? "…" : actionLabel}
        </button>
      )}
    </div>
  );
}

function AvatarChoice({ selected, onClick, label, photo, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display:"flex", alignItems:"center", gap:10,
        padding:"8px 14px 8px 8px",
        background: selected ? `${Y}14` : "transparent",
        border:`1px solid ${selected ? Y : T.border}`,
        borderRadius:24, cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.6 : 1,
        fontFamily:"Mulish,sans-serif",
      }}>
      <div style={{width:28,height:28,borderRadius:"50%",overflow:"hidden",background:T.surface2,display:"flex",alignItems:"center",justifyContent:"center"}}>
        {photo ? <img src={photo} alt="" referrerPolicy="no-referrer" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : <span style={{color:T.text3,fontSize:11}}>?</span>}
      </div>
      <span style={{color: selected ? Y : T.text2, fontSize:12, fontWeight:600}}>{label}</span>
    </button>
  );
}

// Toggle switch component. Supports a separate `inactiveColor` so the
// "off" state can render with its own filled background (used by the
// appearance toggle below where both states are themed colors). Default
// preserves the original transparent-when-off look for callers that
// pass only `activeColor` or rely on the Y default.
function Toggle({ checked, onChange, activeColor = Y, inactiveColor = "transparent", inactiveBorderColor }) {
  const bg = checked ? activeColor : inactiveColor;
  const borderC = checked ? activeColor : (inactiveBorderColor || (inactiveColor === "transparent" ? T.text3 : inactiveColor));
  return (
    <button
      onClick={() => onChange(!checked)}
      className="s-toggle"
      style={{
        width: 40, height: 22, borderRadius: 11, padding: 2,
        border: `1px solid ${borderC}`,
        background: bg,
        cursor: "pointer", display: "flex", alignItems: "center",
        justifyContent: checked ? "flex-end" : "flex-start",
        flexShrink: 0,
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: "50%",
        background: (checked || inactiveColor !== "transparent") ? T.dark : T.text3,
        transition: "background 0.2s",
      }}/>
    </button>
  );
}

export default function SettingsView() {
  const [loading,    setLoading]    = useState(true);
  const [linkedAccounts, setLinkedAccounts] = useState({ google: null, discord: null });
  const [avatarSource,   setAvatarSource]   = useState("google");
  // Whether the Generate page auto-restores the last-used settings on load.
  // Defaults ON (the app's original behavior); off = Generate opens blank.
  const [alwaysPreload,  setAlwaysPreload]  = useState(true);
  const [busy,           setBusy]           = useState(null); // 'link-discord' | 'unlink-discord' | 'link-google' | 'avatar'
  // Provider used for the current session (driven by the ID token's
  // firebase.sign_in_provider claim). 'google.com' for a Google popup,
  // 'custom' for a Discord custom-token sign-in. Falls back to the
  // linked-providers list while the token result is loading so the
  // header doesn't flash empty on first paint.
  const [signedInWith, setSignedInWith] = useState(() => {
    const providers = (auth.currentUser?.providerData || []).map(p => p.providerId);
    return providers.includes('google.com') ? 'Google' : 'Discord';
  });

  useEffect(() => {
    let cancelled = false;
    if (!auth.currentUser) return;
    auth.currentUser.getIdTokenResult().then(r => {
      if (cancelled) return;
      const sip = r.claims && r.claims.firebase && r.claims.firebase.sign_in_provider;
      if (sip === 'google.com') setSignedInWith('Google');
      else if (sip === 'custom') setSignedInWith('Discord');
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const { notify } = useNotify();
  const setError = (msg) => notify("error", msg, 6000);
  const setSuccess = (msg) => notify("success", msg, 3000);
  const setHeaderRight = useHeaderRight();
  const refreshProfile = useRefreshProfile();
  const { mode: themeMode, setMode: setThemeMode } = useTheme();

  const narrow = window.innerWidth <= 600;

  const reload = async () => {
    try {
      const s = await api.getSettings();
      setLinkedAccounts(s.linkedAccounts || { google: null, discord: null });
      setAvatarSource(s.avatarSource || "google");
      setAlwaysPreload(s.alwaysPreloadLastUsedSettings !== false);
    } catch (e) { /* non-fatal */ }
    refreshProfile && refreshProfile();
  };

  // Register signed-in identity in page header. The avatar and
  // sub-label match the provider used to sign in: Discord shows the
  // Discord avatar + username, Google shows the Google avatar + email.
  useEffect(() => {
    const isDiscord = signedInWith === 'Discord';
    const discord   = linkedAccounts && linkedAccounts.discord;
    const googlePhoto = auth.currentUser?.photoURL || null;
    const photo    = isDiscord ? (discord?.avatarUrl || googlePhoto) : googlePhoto;
    const subLabel = isDiscord
      ? (discord?.globalName || discord?.username || auth.currentUser?.email || "\u2014")
      : (auth.currentUser?.email || "\u2014");
    setHeaderRight(
      <div style={{display:"flex",alignItems:"center",gap:10,paddingRight:12}}>
        <div style={{textAlign:"right"}}>
          <div style={{color:T.text2,fontSize:11}}>Signed in with {signedInWith}</div>
          <div style={{color:T.text3,fontSize:11}}>{subLabel}</div>
        </div>
        {!narrow && (
          <div style={{width:32,height:32,borderRadius:"50%",border:`3px solid ${Y}`,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",background:Y,boxSizing:"border-box",flexShrink:0}}>
            {photo ? (
              <img src={photo} alt="" referrerPolicy="no-referrer" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}}/>
            ) : (
              <span style={{color:T.dark,fontSize:13,fontWeight:900}}>{subLabel[0]?.toUpperCase()}</span>
            )}
          </div>
        )}
        <button onClick={()=>signOutUser()} title="Sign out" style={{background:"transparent",border:"none",color:Y,cursor:"pointer",padding:4,display:"flex",borderRadius:"50%",transition:"color 0.15s"}} onMouseEnter={e=>e.currentTarget.style.color="#FF2D6F"} onMouseLeave={e=>e.currentTarget.style.color=Y}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </button>
      </div>
    );
    return () => setHeaderRight(null);
  }, [signedInWith, linkedAccounts, themeMode]);

  useEffect(() => {
    api.getSettings()
      .then(s => {
        setLinkedAccounts(s.linkedAccounts || { google: null, discord: null });
        setAvatarSource(s.avatarSource || "google");
        setAlwaysPreload(s.alwaysPreloadLastUsedSettings !== false);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  // ─── Linked-account actions ────────────────────────────────────────────────
  const onLinkDiscord = async () => {
    setBusy('link-discord');
    try {
      await linkDiscordToCurrentUser();
      await reload();
      setSuccess('Discord account linked');
    } catch (e) {
      setError(e.message || 'Failed to link Discord');
    } finally { setBusy(null); }
  };
  const onUnlinkDiscord = async () => {
    if (!window.confirm('Unlink your Discord account from Katana?')) return;
    setBusy('unlink-discord');
    try {
      await unlinkDiscordFromCurrentUser();
      await reload();
      setSuccess('Discord account unlinked');
    } catch (e) {
      setError(e.message || 'Failed to unlink Discord');
    } finally { setBusy(null); }
  };
  const onLinkGoogle = async () => {
    setBusy('link-google');
    try {
      await linkGoogleToCurrentUser();
      await reload();
      setSuccess('Google account linked');
    } catch (e) {
      setError(e.message || 'Failed to link Google');
    } finally { setBusy(null); }
  };
  const onUnlinkGoogle = async () => {
    if (!window.confirm('Unlink your Google account from Katana? You\'ll sign in with Discord from now on.')) return;
    setBusy('unlink-google');
    try {
      await unlinkGoogleFromCurrentUser();
      await reload();
      setSuccess('Google account unlinked');
    } catch (e) {
      setError(e.message || 'Failed to unlink Google');
    } finally { setBusy(null); }
  };
  const onChooseAvatarSource = async (src) => {
    if (src === avatarSource) return;
    const prev = avatarSource;
    setBusy('avatar');
    setAvatarSource(src);
    try {
      await api.saveAccountSettings({ avatarSource: src });
      refreshProfile && refreshProfile();
      setSuccess('Avatar updated');
    } catch (e) {
      setAvatarSource(prev);
      setError(e.message || 'Failed to save avatar choice');
    } finally { setBusy(null); }
  };

  // ─── Account Section ────────────────────────────────────────────────────────
  const renderAccount = () => {
    const google  = linkedAccounts.google;
    const discord = linkedAccounts.discord;
    const bothLinked = !!(google && discord);
    return (
      <div>
        {/* ═══════ Linked accounts ═══════ */}
        <div style={{marginBottom:32}}>
          <div style={{color:T.title,fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:14}}>Linked accounts</div>
          <div style={{display:"flex",flexDirection:"column",gap:10}}>
            <ProviderRow
              icon={<GoogleIcon/>}
              label="Google"
              detail={google ? google.email : null}
              actionLabel={google ? (discord ? "Unlink" : null) : "Link Google"}
              actionDanger={!!google && !!discord}
              onAction={google ? (discord ? onUnlinkGoogle : null) : onLinkGoogle}
              busy={busy === 'link-google' || busy === 'unlink-google'}
            />
            <ProviderRow
              icon={<DiscordIcon/>}
              label="Discord"
              detail={discord ? (discord.globalName || discord.username || discord.email) : null}
              actionLabel={discord ? "Unlink" : "Link Discord"}
              actionDanger={!!discord}
              onAction={discord ? onUnlinkDiscord : onLinkDiscord}
              busy={busy === 'link-discord' || busy === 'unlink-discord'}
            />
          </div>
        </div>

        {/* ═══════ Avatar source (only relevant when both are linked) ═══════ */}
        {bothLinked && (
          <div style={{marginBottom:32}}>
            <div style={{color:T.title,fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Avatar</div>
            <div style={{color:T.text3,fontSize:12,marginBottom:14}}>Pick which account's avatar shows on the chip.</div>
            <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
              <AvatarChoice
                selected={avatarSource === 'google'}
                onClick={() => onChooseAvatarSource('google')}
                label="Google"
                photo={auth.currentUser?.photoURL}
                disabled={busy === 'avatar'}
              />
              <AvatarChoice
                selected={avatarSource === 'discord'}
                onClick={() => onChooseAvatarSource('discord')}
                label="Discord"
                photo={discord && discord.avatarUrl}
                disabled={busy === 'avatar'}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Appearance Section ─────────────────────────────────────────────────────
  const renderAppearance = () => {
    const isLight = themeMode === 'light';
    const persist = async (next) => {
      // Optimistic local switch first - the avatar-menu toggle does the same
      // so the swap feels instant. Backend persistence is best-effort; if it
      // fails, the local + localStorage value still survives the session.
      setThemeMode(next);
      try { await api.saveAccountSettings({ theme: next }); }
      catch (e) { /* non-fatal */ }
    };
    // Toggle layout: "Light mode" label | toggle | "Dark mode" label.
    // checked = !isLight, so the handle sits on the LEFT under "Light
    // mode" when in light mode, and on the RIGHT under "Dark mode"
    // when in dark. Bg color follows the active mode:
    //   light mode (handle left)  -> turquoise (#06D6A0)
    //   dark mode  (handle right) -> yellow    (#EEFF44)
    const Y_DARK_HEX  = '#EEFF44';
    const Y_LIGHT_HEX = '#06D6A0';
    const labelStyle = (active) => ({
      color: active ? T.text2 : T.text3,
      fontSize: 13,
      fontWeight: active ? 700 : 500,
      cursor: 'pointer',
      userSelect: 'none',
      transition: 'color 0.15s',
    });
    const SunIcon  = ({color}) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/></svg>;
    const MoonIcon = ({color}) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>;
    const lightCol = isLight ? T.text2 : T.text3;
    const darkCol  = !isLight ? T.text2 : T.text3;
    return (
      <div style={{marginBottom:32}}>
        <div style={{color:T.title,fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Appearance</div>
        <div style={{color:T.text3,fontSize:12,marginBottom:14}}>Synced across devices.</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"flex-start",gap:14,padding:"14px 16px",background:"transparent",border:`1px solid ${T.border}`,borderRadius:10}}>
          <span style={{...labelStyle(isLight),display:"inline-flex",alignItems:"center",gap:6}} onClick={() => persist('light')}>
            <SunIcon color={lightCol}/>
            Light mode
          </span>
          <Toggle
            checked={!isLight}
            onChange={v => persist(v ? 'dark' : 'light')}
            activeColor={Y_DARK_HEX}
            inactiveColor={Y_LIGHT_HEX}
          />
          <span style={{...labelStyle(!isLight),display:"inline-flex",alignItems:"center",gap:6}} onClick={() => persist('dark')}>
            Dark mode
            <MoonIcon color={darkCol}/>
          </span>
        </div>
      </div>
    );
  };

  // ─── Generation Section ─────────────────────────────────────────────────────
  const renderGeneration = () => {
    const persist = async (next) => {
      setAlwaysPreload(next);  // optimistic
      try { await api.saveAccountSettings({ alwaysPreloadLastUsedSettings: next }); }
      catch (e) { /* non-fatal */ }
    };
    return (
      <div style={{marginBottom:32}}>
        <div style={{color:T.title,fontSize:11,fontWeight:700,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:6}}>Generation</div>
        <div style={{color:T.text3,fontSize:12,marginBottom:14}}>Synced across devices.</div>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:14,padding:"14px 16px",border:`1px solid ${T.border}`,borderRadius:10}}>
          <div style={{minWidth:0}}>
            <div style={{color:T.text2,fontSize:13,fontWeight:600}}>Always preload last used generation settings</div>
            <div style={{color:T.text3,fontSize:12,marginTop:4,lineHeight:1.5}}>When on, the Generate page restores your most recent prompt, model, and inputs on load. When off, it opens blank - settings load only when you use Reuse or a saved preset.</div>
          </div>
          <Toggle checked={alwaysPreload} onChange={persist}/>
        </div>
      </div>
    );
  };

  // ─── Main Layout ────────────────────────────────────────────────────────────
  return (
    <div>
      <PlaceholderStyle/>
      {/* One loader for the whole page so every section (including the
          Appearance / theme-toggle row) stays hidden until settings
          finish loading, instead of only gating the account section. */}
      <PageLoader ready={!loading}/>
      {renderAccount()}
      {renderAppearance()}
      {renderGeneration()}
    </div>
  );
}
